import { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const GENRES = ["Novela", "Ensayo", "Guía práctica", "Poemario", "Autoayuda", "Relato", "Otro"];

let fileSeq = 0;

export default function BookForm({ onCreated }) {
  const [form, setForm] = useState({ title: "", author_name: "", genre: "Novela", synopsis: "", mode: "estandar", book_format: "pocket_425x7", author_bio: "" });
  const [files, setFiles] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [layout, setLayout] = useState("");
  const [modes, setModes] = useState([]);
  const [formats, setFormats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const dragId = useRef(null);

  useEffect(() => { api.get("/config").then((r) => {
      setModes(r.data.modes);
      if (r.data.formats) setFormats(r.data.formats);
      if (r.data.default_format) setForm((f) => ({ ...f, book_format: r.data.default_format }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const detectedInfo = files.find((f) => f.detected)?.detected || null;

  // Al indicar que ya está maquetado, adoptamos el tamaño detectado del PDF.
  useEffect(() => {
    if (layout === "maquetado" && detectedInfo?.format_key) {
      set("book_format", detectedInfo.format_key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, detectedInfo?.format_key]);

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setUploading(true);
    for (const file of arr) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, { id: ++fileSeq, name: data.filename, text: data.text, detected: data.detected || null }]);
      } catch (e) {
        toast.error(`${file.name}: ${formatError(e.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    toast.success(arr.length > 1 ? "Materiales cargados. Puedes reordenarlos." : "Material cargado.");
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const moveFile = (from, to) => setFiles((prev) => {
    if (to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
  });

  const onDrop = (index) => {
    const from = files.findIndex((f) => f.id === dragId.current);
    dragId.current = null;
    if (from >= 0 && from !== index) moveFile(from, index);
  };

  const combinedText = () => {
    const parts = files.map((f) => f.text);
    if (pasteText.trim()) parts.push(pasteText.trim());
    return parts.join("\n\n").trim();
  };

  const validate = () => {
    const er = {};
    if (!form.title.trim()) er.title = "Ponle un título.";
    if (!form.author_name.trim()) er.author_name = "Dinos quién firma el libro.";
    if (combinedText().length < 20) er.manuscript = "Sube tu material para empezar.";
    if (!layout) er.layout = "Dinos si tu libro ya está maquetado.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        manuscript_text: combinedText(),
        manuscript_filename: files[0]?.name || null,
        manuscript_filenames: files.map((f) => f.name),
        layout_status: layout,
        source_pdf_paths: layout === "maquetado" ? files.filter((f) => f.detected?.source_path).map((f) => f.detected.source_path) : [],
      };
      const { data } = await api.post("/projects", payload);
      onCreated(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const modeList = modes.length ? modes : [{ key: "estandar", label: "Estándar", price: 49, descripcion: "" }, { key: "complejo", label: "Complejo", price: 79, descripcion: "" }];
  const formatList = formats.length ? formats : [
    { key: "us_5x8", label: "5 × 8 pulgadas", cm: "12,7 × 20,32 cm", descripcion: "Narrativa internacional", suggested: false },
    { key: "us_525x8", label: "5.25 × 8 pulgadas", cm: "13,34 × 20,32 cm", descripcion: "Narrativa ligera", suggested: false },
    { key: "us_55x85", label: "5.5 × 8.5 pulgadas", cm: "13,97 × 21,59 cm", descripcion: "Editorial clásico", suggested: false },
    { key: "a5", label: "A5", cm: "14,8 × 21 cm", descripcion: "Estándar europeo", suggested: false },
    { key: "pocket_425x7", label: "4.25 × 7 pulgadas", cm: "10,79 × 17,78 cm", descripcion: "Bolsillo pequeño", suggested: true },
    { key: "mini_4x6", label: "4 × 6 pulgadas", cm: "10,16 × 15,24 cm", descripcion: "Mini", suggested: false },
  ];
  const selectedFormat = formatList.find((f) => f.key === form.book_format);

  // Aviso sobre la detección del tamaño cuando el libro ya está maquetado.
  let layoutNotice = null;
  if (layout === "maquetado") {
    if (detectedInfo) {
      layoutNotice = detectedInfo.matches_official
        ? { kind: "ok", text: `Detectamos que tu libro está maquetado a ${detectedInfo.format_label} (${detectedInfo.size_cm}). Mantendremos ese tamaño.` }
        : { kind: "warn", text: `Tu maqueta mide ${detectedInfo.size_cm}, que no es un tamaño de publicación estándar. La adaptaremos a ${detectedInfo.format_label} para que sea válida para imprenta y tiendas.` };
    } else {
      layoutNotice = { kind: "info", text: "Sube el PDF ya maquetado para detectar su tamaño automáticamente, o elige el formato a la derecha." };
    }
  }

  const noticeColors = { ok: ["#1F6B45", "#eaf5ef"], warn: ["#8a5a00", "#fbf1dd"], info: ["#1C3B73", "#eaf0fb"] };

  return (
    <div className="panel">
      <div className="split">
        <div className="stack">
          <div className="row2">
            <label className="field"><span>Título</span>
              <input data-testid="book-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={200} />
              {errors.title && <span className="err">{errors.title}</span>}
            </label>
            <label className="field"><span>Autor</span>
              <input data-testid="book-author-input" value={form.author_name} onChange={(e) => set("author_name", e.target.value)} maxLength={120} />
              {errors.author_name && <span className="err">{errors.author_name}</span>}
            </label>
          </div>

          <div className="field"><span>Género</span>
            <div className="chips" role="radiogroup" aria-label="Género" data-testid="book-genre-chips">
              {GENRES.map((g) => (
                <button type="button" key={g} data-testid={`book-genre-chip-${g.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-")}`} className={`chip-btn ${form.genre === g ? "on" : ""}`} aria-pressed={form.genre === g} onClick={() => set("genre", g)}>{g}</button>
              ))}
            </div>
          </div>

          <label className="field"><span>Sinopsis breve <small style={{ color: "var(--muted)", fontWeight: 400 }}>· opcional · {form.synopsis.length}/400</small></span>
            <textarea data-testid="book-synopsis-input" rows={3} maxLength={400} value={form.synopsis} onChange={(e) => set("synopsis", e.target.value)} />
          </label>

          <label className="field"><span>Sobre el autor <small style={{ color: "var(--muted)", fontWeight: 400 }}>· opcional · breve biografía para la hoja final · {form.author_bio.length}/600</small></span>
            <textarea data-testid="book-author-bio-input" rows={3} maxLength={600} value={form.author_bio} onChange={(e) => set("author_bio", e.target.value)} placeholder="Unas líneas sobre el autor (aparecerán en la página «Sobre el autor»)." />
          </label>

          <label className="drop-zone" data-testid="book-file-upload-dropzone">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1C3B73" strokeWidth="1.6"><path d="M12 16V4M6 10l6-6 6 6" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></svg>
            <strong>{uploading ? "Cargando…" : "Arrastra o elige tu material"}</strong>
            <small style={{ color: "var(--muted)" }}>.docx, .pdf o .txt · puedes subir varios archivos</small>
            <input type="file" multiple accept=".txt,.md,.pdf,.docx" style={{ display: "none" }} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          </label>

          {files.length > 0 && (
            <div className="stack" style={{ gap: 8 }} data-testid="uploaded-files-list">
              {files.length > 1 && <small style={{ color: "var(--muted)" }}>Arrastra para ordenar · se combinarán en este orden</small>}
              {files.map((f, i) => (
                <div
                  key={f.id}
                  className="file"
                  draggable
                  onDragStart={() => { dragId.current = f.id; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  data-testid={`uploaded-file-${i}`}
                  style={{ cursor: "grab", display: "flex", alignItems: "center", gap: 10 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9aa2b1" strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></svg>
                  <span style={{ fontFamily: "var(--serif)", color: "var(--muted)", fontSize: 13, flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</strong>
                    <small style={{ color: "var(--muted)" }}>{f.detected ? `Maqueta detectada · ${f.detected.size_cm}` : "Material cargado"}</small>
                  </span>
                  <button type="button" onClick={() => moveFile(i, i - 1)} disabled={i === 0} aria-label="Subir" title="Subir" style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #e2d9cb", background: "transparent", color: "#1C3B73", cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.3 : 1, flexShrink: 0 }}>↑</button>
                  <button type="button" onClick={() => moveFile(i, i + 1)} disabled={i === files.length - 1} aria-label="Bajar" title="Bajar" style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #e2d9cb", background: "transparent", color: "#1C3B73", cursor: i === files.length - 1 ? "default" : "pointer", opacity: i === files.length - 1 ? 0.3 : 1, flexShrink: 0 }}>↓</button>
                  <button type="button" data-testid={`remove-file-btn-${i}`} onClick={() => removeFile(f.id)} aria-label="Eliminar material" title="Eliminar material" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid #e2d9cb", background: "transparent", color: "#b4443c", cursor: "pointer", flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="field">
            <span>¿Tu libro ya está maquetado?</span>
            <div className="chips" role="radiogroup" aria-label="Estado de maquetación" data-testid="layout-status-chips">
              <button type="button" data-testid="layout-maquetado" className={`chip-btn ${layout === "maquetado" ? "on" : ""}`} aria-pressed={layout === "maquetado"} onClick={() => setLayout("maquetado")}>Sí, ya está maquetado</button>
              <button type="button" data-testid="layout-sin-maquetar" className={`chip-btn ${layout === "sin_maquetar" ? "on" : ""}`} aria-pressed={layout === "sin_maquetar"} onClick={() => setLayout("sin_maquetar")}>Aún no</button>
            </div>
            {errors.layout && <span className="err">{errors.layout}</span>}
            {layoutNotice && (
              <div data-testid="layout-notice" style={{ marginTop: 8, padding: "10px 12px", borderRadius: 10, fontSize: 13, lineHeight: 1.45, color: noticeColors[layoutNotice.kind][0], background: noticeColors[layoutNotice.kind][1] }}>
                {layoutNotice.text}
              </div>
            )}
          </div>

          <label className="field"><span>Texto del material <small style={{ color: "var(--muted)", fontWeight: 400 }}>· opcional · pega aquí si prefieres no subir archivo</small></span>
            <textarea data-testid="book-manuscript-textarea" rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder="Pega aquí tu texto si prefieres no subir archivo." />
            {errors.manuscript && <span className="err">{errors.manuscript}</span>}
          </label>
        </div>

        <div className="stack">
          <span className="side-label">Modo editorial</span>
          {modeList.map((m) => (
            <button type="button" key={m.key} data-testid={`mode-${m.key}-option`} className={`mode ${form.mode === m.key ? "on" : ""}`} onClick={() => set("mode", m.key)}>
              <span style={{ display: "flex", justifyContent: "space-between" }}><strong>{m.label}</strong><span className="num" style={{ fontFamily: "var(--serif)", fontSize: 22 }}>{m.price} €</span></span>
              <small>{m.descripcion}</small>
            </button>
          ))}

          <span className="side-label" style={{ marginTop: 6 }}>Formato del libro</span>
          <div className="field" style={{ gap: 6 }}>
            <select
              data-testid="book-format-select"
              value={form.book_format}
              onChange={(e) => set("book_format", e.target.value)}
              aria-label="Formato del libro"
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #e2d9cb", background: "#fff", color: "var(--ink, #15233f)", fontSize: 14, fontFamily: "inherit", cursor: "pointer", appearance: "auto" }}
            >
              {formatList.map((fmt) => (
                <option key={fmt.key} value={fmt.key}>
                  {fmt.label} · {fmt.cm} — {fmt.descripcion}{fmt.suggested ? " (sugerido)" : ""}
                </option>
              ))}
            </select>
            {selectedFormat && (
              <small style={{ color: "var(--muted)" }} data-testid="book-format-hint">
                {selectedFormat.cm} · {selectedFormat.descripcion}
                {selectedFormat.suggested && <strong style={{ color: "#1C3B73" }}> · el más usado</strong>}
              </small>
            )}
          </div>

          <div className="tip"><strong>Consejo</strong> Sube .docx para conservar cursivas y títulos de capítulo.</div>
        </div>
      </div>

      <div className="panel-foot">
        <span style={{ color: "var(--muted)", fontSize: 14 }}>No se cobra nada en este paso.</span>
        <button type="button" data-testid="submit-book-data-btn" className="btn btn-primary" onClick={submit} disabled={saving}>
          {saving ? "Preparando…" : "Analizar y generar borrador"}
        </button>
      </div>
    </div>
  );
}
